<script setup lang="ts">
import CoreChipAction from 'frontcore/views/chips/chip/views/CoreChipAction.vue'
import CoreChipInput from 'frontcore/views/chips/chip/views/CoreChipInput.vue'
import i18n from 'frontcore/i18n'
import useRemoteFiltersStore from 'frontcore/presentation/remote-filters/data/use-remote-filters-store'
import { computed } from 'vue'
import {
  RemoteFiltersBooleanValue,
  RemoteFiltersDateRangeValue,
  RemoteFiltersDateValue,
  RemoteFiltersOptionListValue,
  RemoteFiltersOptionValue,
  RemoteFiltersValue,
} from 'frontcore/presentation/remote-filters/domain/remote-filters-value'
import {
  RemoteFiltersChip,
  RemoteFiltersFieldType,
} from 'frontcore/presentation/remote-filters/domain/types'

type Props = {
  filterStoreID: string
}
const props = defineProps<Props>()

type Emits = (e: 'selectFilterItem') => void
const emit = defineEmits<Emits>()

const filtersStore = computed(() => {
  return useRemoteFiltersStore(props.filterStoreID)
})

const { t } = i18n.global

const wrapperStyleClass = (chip: RemoteFiltersChip, chips: RemoteFiltersChip[]): string[] => {
  const styleClass: string[] = ['tw-flex tw-items-center tw-h-10', 'tw-flex-none']
  if (chip.id === chips.at(0)?.id) {
    styleClass.push('remote-filters-chips__wrapper--first')
  }
  if (chip.id === chips.at(-1)?.id) {
    styleClass.push('tw-mr-2')
  }
  return styleClass
}

const isTitleVisible = (chip: RemoteFiltersChip, index: number): boolean => {
  return (chip.filterType !== RemoteFiltersFieldType.SWITCH && chip.filterType !== RemoteFiltersFieldType.SEGMENTED_BUTTON) && index === 0
}

const hasChanges = computed(() => {
  return Object.values(filtersStore.value.stagedFilters)
    .some((filter: RemoteFiltersValue) => filtersStore.value.isFilterChanged(filter.configuration))
})

const onSelectFilterItem = (): void => {
  emit('selectFilterItem')
}

const onDeleteFilterItem = (chip: RemoteFiltersChip) => {
  const remoteFiltersValue = filtersStore.value.getRemoteFiltersValue(chip.filterId)
  if (remoteFiltersValue === undefined) { return }
  if (remoteFiltersValue instanceof RemoteFiltersBooleanValue) {
    remoteFiltersValue.value = false
  } else if (remoteFiltersValue instanceof RemoteFiltersDateRangeValue) {
    remoteFiltersValue.value = []
  } else if (remoteFiltersValue instanceof RemoteFiltersDateValue) {
    remoteFiltersValue.value = undefined
  } else if (remoteFiltersValue instanceof RemoteFiltersOptionListValue) {
    remoteFiltersValue.value = remoteFiltersValue.value.filter((rfv) => rfv !== chip.id)
  } else if (remoteFiltersValue instanceof RemoteFiltersOptionValue) {
    remoteFiltersValue.value = undefined
  }
  filtersStore.value.commitChanges()
}
</script>

<template>
  <div
    class="remote-filters-chips tw-flex tw-flex-wrap tw-items-start"
    data-test="active-filter-section">
    <template v-for="(chips, title) in filtersStore.remoteFiltersChips">
      <div
        v-for="(chip, index) in chips"
        :key="`${chip.filterId}.${chip.id}`"
        :class="wrapperStyleClass(chip, chips)">
        <div class="remote-filters-chips__chip tw-bg-core-primary-99 tw-flex tw-items-center">
          <span
            v-if="isTitleVisible(chip, index)"
            class="tw-pr-1 tw-pl-2 body--auxiliary-01 tw-text-core-primary-10">{{ title }}</span>
          <CoreChipInput
            :dataTest="`remote-filters-chip-${chip.filterId}-${chip.id}`"
            :deletable="!chip.required"
            :label="chip.text"
            selected
            @click="onSelectFilterItem"
            @handlerClick="() => onDeleteFilterItem(chip)" />
        </div>
      </div>
    </template>
    <div class="tw-flex tw-items-center tw-h-10">
      <CoreChipAction
        v-if="hasChanges"
        dataTest="reset-filters-button"
        class="remote-filters-chips__reset tw-flex-none"
        iconName="RefreshFill"
        :label="t('common.messages.reset')"
        @click="filtersStore.clearChanges" />
    </div>
  </div>
</template>

<style scoped>

.remote-filters-chips__wrapper--first .remote-filters-chips__chip {
  padding-left: 0;
  border-top-left-radius: var(--s-1);
  border-bottom-left-radius: var(--s-1);
}

.remote-filters-chips__chip {
  padding: 0.125rem;
}

.remote-filters-chips__wrapper--last .remote-filters-chips__chip {
  border-top-right-radius: var(--s-1);
  border-bottom-right-radius: var(--s-1);
}

.remote-filters-chips__reset {
  margin-top: 0.125rem;
}
</style>
